import React, { useState, useEffect, useCallback } from 'react';
import { gameNotice } from './oilNotice';
import { oilApi, genCmd, fmt, reasonMsg } from './oilApi';
import { sfx, playClip, sfxClose, sfxTab } from './oilSounds';

const SFX_SPEND = '/oil/sfx_gasto.mp3';
import { OilIcon } from './OilIcons';
import { useOilLocale } from '../../i18n/OilLocale';
import { oilArt } from '../../i18n/oilArt';
import OilProcessVideo from './OilProcessVideo';

const lvlCfg = (arr, lvl) => (arr || []).find((l) => l.level === lvl);

const integTone = (v) => {
  if (v <= 0) return { bar: 'bg-red-600', txt: 'text-red-300', seal: 'border-red-500 text-red-300', anim: 'oil-integ-broken 1.1s ease-in-out infinite' };
  if (v < 50) return { bar: 'bg-orange-500', txt: 'text-orange-300', seal: 'border-orange-500 text-orange-300', anim: 'oil-integ-risk 2.4s ease-in-out infinite' };
  if (v < 100) return { bar: 'bg-amber-400', txt: 'text-amber-300', seal: 'border-amber-500/80 text-amber-300', anim: 'none' };
  return { bar: 'bg-emerald-500', txt: 'text-emerald-300', seal: 'border-emerald-600/80 text-emerald-300', anim: 'none' };
};

// selo fixo sobre a arte da instalação — mostra a integridade do nível na tela
const IntegritySeal = ({ kind, value }) => {
  const t = integTone(value);
  return (
    <div data-testid={`oil-integrity-seal-${kind}`} style={{ animation: t.anim }}
      className={`absolute left-3 top-1 z-10 rounded-lg border bg-black/85 px-2 py-1 text-center shadow-xl backdrop-blur ${t.seal}`}>
      <p className="text-[7px] font-black uppercase tracking-[0.18em] text-zinc-400">Integridade</p>
      <p className="text-[11px] font-black leading-tight" data-testid={`oil-integrity-seal-value-${kind}`}>
        {value <= 0 ? 'QUEBRADA' : `${value}%`}
      </p>
    </div>
  );
};

const BrokenStamp = ({ kind }) => (
  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" data-testid={`oil-broken-stamp-${kind}`}>
    <span className="-rotate-12 rounded-md border-2 border-red-500 bg-black/75 px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-red-400 shadow-2xl"
      style={{ animation: 'oil-integ-broken 1.4s ease-in-out infinite' }}>
      Fora de operação
    </span>
  </div>
);

// seletor de quantidade do lote (máx 50 por toque)
const QtyPicker = ({ kind, qty, setQty, max, tone }) => {
  const cap = Math.max(1, Math.min(50, max));
  const btn = `rounded-lg border px-0 py-1 text-[10px] font-black uppercase transition-transform active:scale-95 ${
    tone === 'emerald' ? 'border-emerald-700/70 bg-emerald-950/50 text-emerald-300'
      : 'border-amber-700/70 bg-amber-950/50 text-amber-300'}`;
  return (
    <div className="mb-2 flex items-center gap-1" data-testid={`oil-qty-${kind}`}>
      <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid={`oil-qty-minus-${kind}`}
        className={`${btn} w-8 text-sm`}>−</button>
      <span className="min-w-9 rounded-lg border border-zinc-700 bg-black/70 px-2 py-1 text-center text-xs font-black text-white"
        data-testid={`oil-qty-value-${kind}`}>{qty}</span>
      <button onClick={() => setQty(Math.min(cap, qty + 1))} data-testid={`oil-qty-plus-${kind}`}
        className={`${btn} w-8 text-sm`}>+</button>
      {[1, 5, 10].map((n) => (
        <button key={n} onClick={() => setQty(Math.min(cap, n))} data-testid={`oil-qty-x${n}-${kind}`}
          className={`${btn} flex-1`}>x{n}</button>
      ))}
      <button onClick={() => setQty(cap)} data-testid={`oil-qty-max-${kind}`}
        className={`${btn} flex-1`}>Max</button>
    </div>
  );
};

const Integrity = ({ kind, level, value, cost, onRepair, busy, coins }) => {
  const broken = value <= 0;
  const t = integTone(value);
  const missing = 100 - value;
  const short = cost > coins;
  return (
    <div className="mt-2 rounded-xl border border-zinc-800 bg-black/60 p-2" data-testid={`oil-integrity-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">
          Integridade LV.{level}
        </span>
        <span className={`text-[10px] font-black ${broken ? 'text-red-400' : t.txt}`}
          data-testid={`oil-integrity-value-${kind}`}>
          {broken ? 'FORA DE OPERAÇÃO' : `${value}%`}
        </span>
      </div>
      <div className="relative mt-1 h-2.5 w-full overflow-hidden rounded-full bg-zinc-800">
        <div className={`h-full rounded-full transition-[width] duration-500 ${t.bar}`}
          style={{ width: `${Math.max(0, value)}%` }} />
      </div>
      <p className="mt-1 text-[8.5px] font-bold leading-tight text-zinc-500" data-testid={`oil-integrity-hint-${kind}`}>
        Ataques da Máfia derrubam a integridade. Em 0 a instalação para até o reparo.
      </p>
      {missing > 0 && (
        <>
          <button onClick={onRepair} disabled={busy || short} data-testid={`oil-repair-${kind}`}
            style={broken && !short ? { animation: 'oil-integ-broken 1.3s ease-in-out infinite' } : undefined}
            className={`mt-2 flex w-full flex-wrap items-center justify-center gap-1.5 rounded-lg border py-1.5 text-[10px] font-black uppercase tracking-widest transition-transform active:scale-95 disabled:active:scale-100 ${
              short ? 'border-zinc-700 bg-zinc-900/70 text-zinc-500'
                : broken ? 'border-red-500 bg-red-950/80 text-red-200'
                  : 'border-amber-500/80 bg-amber-950/70 text-amber-300'}`}>
            Reparar
            <span className="rounded-full bg-black px-2 py-0.5 text-[9px] font-black text-emerald-300">
              +{missing} de integridade
            </span>
            <span className="flex items-center gap-1 rounded-full bg-black px-2 py-0.5 text-[9px] font-black text-red-400">
              -{fmt(cost)} <OilIcon k="black" />
            </span>
          </button>
          {short && (
            <p className="mt-1 text-center text-[9px] font-black uppercase text-red-400" data-testid={`oil-repair-short-${kind}`}>
              Faltam {fmt(cost - coins)} Black Coins para o reparo
            </p>
          )}
        </>
      )}
    </div>
  );
};

function OilEmpire({ state, refresh }) {
  const { player, config } = state;
  const { locale } = useOilLocale();
  const [busy, setBusy] = useState(false);
  const [drilling, setDrilling] = useState(false);
  const [lastDrops, setLastDrops] = useState(null);
  const [barrels, setBarrels] = useState([]);
  const [proc, setProc] = useState(null);
  const [subTab, setSubTab] = useState('usina');
  const [selLvl, setSelLvl] = useState(null);
  const [selBotLvl, setSelBotLvl] = useState(null);
  const [sellLvl, setSellLvl] = useState(null);
  const [sellQty, setSellQty] = useState(1);
  const [exQty, setExQty] = useState(1);
  const [botQty, setBotQty] = useState(1);
  const [fly, setFly] = useState([]);
  const [warnKind, setWarnKind] = useState(null);
  const [warnNever, setWarnNever] = useState(false);

  const launchFly = (icon, fromSel, toSel, n) => {
    const from = document.querySelector(fromSel)?.getBoundingClientRect();
    const to = document.querySelector(toSel)?.getBoundingClientRect();
    if (!from || !to) return;
    const parts = Array.from({ length: Math.max(3, Math.min(n, 10)) }, (_, i) => {
      const x = from.left + from.width / 2 + (Math.random() - 0.5) * from.width * 0.5;
      const y = from.top + from.height / 2 + (Math.random() - 0.5) * from.height * 0.3;
      return { id: `${Date.now()}-${i}`, icon, x, y, dx: to.left + to.width / 2 - x, dy: to.top + to.height / 2 - y, delay: i * 90 };
    });
    setFly(parts);
    setTimeout(() => setFly([]), 1100 + parts.length * 90);
  };
  const launchDrops = (n) => launchFly('drops', '[data-testid="oil-usina-img"]', '[data-testid="oil-hud-drops"]', n);

  const loadBarrels = useCallback(async () => {
    try { const r = await oilApi.get('/barrels'); setBarrels(r.data.barrels || []); } catch {}
  }, []);
  useEffect(() => { loadBarrels(); }, [loadBarrels]);
  useEffect(() => { setExQty(1); }, [selLvl, subTab]);
  useEffect(() => { setBotQty(1); }, [selBotLvl, subTab]);

  const run = async (fn, onOk, onFail) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r.data.ok) { onOk?.(r.data); refresh(); loadBarrels(); }
      else { gameNotice.fail('Operação recusada', reasonMsg(r.data)); onFail?.(); }
    } catch (e) { gameNotice.fail('Erro de conexão', e.response?.data?.detail || 'Tenta de novo em instantes.'); onFail?.(); }
    setBusy(false);
  };

  const refLvl = player.refinery_level || 0;
  const botLvl = player.bottling_level || 0;
  const integrity = (kind, level) => {
    const v = (player.facility_integrity || {})[kind]?.[String(level)];
    return v === undefined || v === null ? 100 : Number(v);
  };
  const repairPct = Number(config.limits?.facility_repair_pct_of_price || 20);
  const repairCost = (kind, level) => {
    const cfg = (config.facilities?.[kind] || []).find((f) => f.level === level);
    const missing = 100 - integrity(kind, level);
    if (!cfg || missing <= 0) return 0;
    return Math.max(1, Math.floor(cfg.purchase_or_upgrade_black_coins * repairPct * missing / 10000));
  };
  const doRepair = (kind, level) => run(
    () => oilApi.post(`/repair/${kind}`, { command_id: genCmd(), level }),
    (d) => {
      playClip(SFX_SPEND); sfx.build();
      gameNotice.ok('Reparo concluído!',
        `${kind === 'refinery' ? 'Usina' : 'Envasadora'} LV${d.level} voltou a operar. `
        + `Integridade agora em ${d.integrity ?? 100}%. Custou ${fmt(d.cost)} Black Coins.`);
    });

  const maxLvl = (config.facilities.refinery || []).length || 5;
  const lvl = selLvl ?? Math.max(refLvl, 1);
  const refInteg = integrity('refinery', lvl);
  const selCfg = lvlCfg(config.facilities.refinery, lvl);
  const owned = lvl <= refLvl;
  const isNext = lvl === refLvl + 1;
  const refCur = lvlCfg(config.facilities.refinery, refLvl);
  const refNext = lvlCfg(config.facilities.refinery, refLvl + 1);
  const botCur = lvlCfg(config.facilities.bottling, botLvl);
  const botNext = lvlCfg(config.facilities.bottling, botLvl + 1);
  const maxBotLvl = (config.facilities.bottling || []).length || 5;
  const bLvl = selBotLvl ?? Math.max(botLvl, 1);
  const bCfg = lvlCfg(config.barrels, bLvl);
  const botSelCfg = lvlCfg(config.facilities.bottling, bLvl);
  const bOwned = bLvl <= botLvl;
  const botInteg = integrity('bottling', bLvl);
  const bIsNext = bLvl === botLvl + 1;
  const canBottle = (player.drops || 0) >= (bCfg?.oil_drops_required || 0);
  const invCount = barrels.reduce((m, b) => { m[b.level] = (m[b.level] || 0) + 1; return m; }, {});
  const unitVal = lvlCfg(config.barrels, sellLvl)?.fox_dollar_value || 0;

  // resumo: pior nível de cada instalação, quando quebrado ou abaixo de 50%
  const worstLevel = (kind, top) => {
    let best = null;
    for (let n = 1; n <= top; n += 1) {
      const v = integrity(kind, n);
      if (best === null || v < best.value) best = { kind, level: n, value: v };
    }
    return best;
  };
  const integAlerts = [worstLevel('refinery', refLvl), worstLevel('bottling', botLvl)]
    .filter((a) => a && a.value < 50);

  // lotes: quanto cabe no bolso agora (teto de 50 por toque)
  const exCost = selCfg?.extraction_cost_black_coins || 0;
  const exMax = exCost > 0 ? Math.min(50, Math.floor((player.black_coins || 0) / exCost)) : 0;
  const exQtyEff = Math.max(1, Math.min(exQty, Math.max(1, exMax)));
  const exTotal = exCost * exQtyEff;
  const exShort = Math.max(0, exTotal - (player.black_coins || 0));
  const canExtract = exShort === 0;
  const botCost = bCfg?.oil_drops_required || 0;
  const botMax = botCost > 0 ? Math.min(50, Math.floor((player.drops || 0) / botCost)) : 0;
  const botQtyEff = Math.max(1, Math.min(botQty, Math.max(1, botMax)));

  const doExtract = () => {
    if (busy || drilling || !owned || refLvl === 0 || !canExtract) return;
    if (!localStorage.getItem('oil_warn_never') && !localStorage.getItem(`oil_warn_seen_lv${lvl}`)) {
      setWarnNever(false); setWarnKind('extract');
      return;
    }
    runExtract();
  };

  const runBottle = () => {
    playClip('/oil/sfx_gotas.mp3'); // gotas gastas no clique de envasar
    setProc({ kind: 'bottle', level: bLvl, result: null });
    run(() => oilApi.post('/bottle', { command_id: genCmd(), barrel_level: bLvl, quantity: botQtyEff }),
      (d) => setProc((p) => (p ? { ...p, result: { level: bLvl, units: d.units || 1, drops: d.drops_spent } } : p)),
      () => setProc(null));
  };

  const doBottle = () => {
    if (busy) return;
    if (!localStorage.getItem('oil_warn_bottle_never') && !localStorage.getItem(`oil_warn_bottle_seen_lv${bLvl}`)) {
      setWarnNever(false); setWarnKind('bottle');
      return;
    }
    runBottle();
  };

  const confirmWarn = () => {
    if (warnKind === 'extract') {
      localStorage.setItem(`oil_warn_seen_lv${lvl}`, '1');
      if (warnNever) localStorage.setItem('oil_warn_never', '1');
      setWarnKind(null);
      runExtract();
    } else {
      localStorage.setItem(`oil_warn_bottle_seen_lv${bLvl}`, '1');
      if (warnNever) localStorage.setItem('oil_warn_bottle_never', '1');
      setWarnKind(null);
      runBottle();
    }
  };

  const runExtract = () => {
    setLastDrops(null);
    setProc({ kind: 'extract', level: lvl, result: null });
    run(() => oilApi.post('/extract', { command_id: genCmd(), level: lvl, quantity: exQtyEff }), (d) => {
      playClip(SFX_SPEND); // moedas pretas da extração
      setProc((p) => (p ? { ...p, result: { drops: d.drops, units: d.units || 1, dry: d.dry || 0 } } : p));
    }, () => setProc(null));
  };

  const onProcReveal = (r) => {
    if (proc?.kind === 'extract') {
      setLastDrops(r.drops);
      if (r.drops === 0) sfx.dry();
    } else {
      playClip('/oil/sfx_barril.mp3'); // barril creditado
    }
  };

  const closeProc = () => {
    const p = proc;
    setProc(null);
    if (p?.kind === 'extract' && p.result?.drops > 0) {
      setTimeout(() => launchDrops(p.result.drops), 80);
    }
  };

  return (
    <div className="relative" data-testid="oil-empire">
      {proc && (
        <OilProcessVideo kind={proc.kind} result={proc.result} barrelLevel={proc.level}
          onReveal={onProcReveal} onClose={closeProc} />
      )}
      {warnKind && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm" data-testid="oil-extract-warn-card">
          <div className="w-full max-w-xs rounded-2xl border border-amber-600/70 bg-gradient-to-b from-[#1a1305] to-[#0c0903] p-5 shadow-2xl">
            <div className="flex justify-center"><OilIcon k={warnKind === 'extract' ? 'black' : 'drops'} className="!h-12" /></div>
            <p className="mt-3 text-center text-sm font-black text-amber-300">CUSTO DE {warnKind === 'extract' ? 'EXTRAÇÃO' : 'ENVASE'}</p>
            <p className="mt-2 text-center text-xs leading-relaxed text-zinc-300">
              {warnKind === 'extract' ? (
                <>Cada extração na <b className="text-white">Usina LV.{lvl}</b> consome{' '}
                  <b className="whitespace-nowrap text-amber-300">{fmt(selCfg?.extraction_cost_black_coins)} Moedas Pretas</b> do seu saldo.</>
              ) : (
                <>Cada envase na <b className="text-white">Envasadora LV.{bLvl}</b> consome{' '}
                  <b className="whitespace-nowrap text-amber-300">{fmt(bCfg?.oil_drops_required)} Gotas de Petróleo</b> do seu saldo.</>
              )}
            </p>
            <label className="mx-auto mt-4 flex w-fit cursor-pointer items-center gap-2 rounded-full border border-amber-600/60 bg-amber-950/40 px-3.5 py-2 text-[11px] font-bold text-amber-200"
              style={{ animation: warnNever ? 'none' : 'warnpulse 1.6s ease-in-out infinite' }}>
              <input type="checkbox" checked={warnNever} onChange={(e) => setWarnNever(e.target.checked)}
                data-testid="oil-extract-warn-never" className="h-4 w-4 accent-amber-500" />
              Não exibir este aviso novamente
            </label>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setWarnKind(null)} data-testid="oil-extract-warn-cancel"
                className="flex-1 rounded-xl border border-zinc-700 bg-black/60 py-2.5 text-xs font-black text-zinc-300 transition-transform active:scale-95">
                CANCELAR
              </button>
              <button onClick={confirmWarn} data-testid="oil-extract-warn-confirm"
                className="flex-1 rounded-xl bg-gradient-to-b from-amber-400 to-orange-600 py-2.5 text-xs font-black text-black shadow transition-transform active:scale-95">
                {warnKind === 'extract' ? 'EXTRAIR' : 'ENVASAR'}
              </button>
            </div>
          </div>
        </div>
      )}
      {fly.map((p) => (
        <img key={p.id} src={`/oil/ic_${p.icon}.webp`} alt="" className="pointer-events-none fixed z-[80] h-6 w-auto opacity-0"
          style={{ left: p.x, top: p.y, '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, animation: `oilfly 0.9s cubic-bezier(0.35,0,0.75,0.45) ${p.delay}ms forwards` }} />
      ))}
      <div className="relative space-y-4 pb-6 pt-1">
        {/* ===== RESUMO DE INTEGRIDADE ===== */}
        {integAlerts.length > 0 && (
          <div className="flex flex-col gap-1.5" data-testid="oil-integrity-summary">
            {integAlerts.map((a) => (
              <button key={a.kind} data-testid={`oil-integrity-summary-${a.kind}`}
                onClick={() => { sfxTab(); setSubTab(a.kind === 'refinery' ? 'usina' : 'envasadora');
                  if (a.kind === 'refinery') setSelLvl(a.level); else setSelBotLvl(a.level); }}
                className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-transform active:scale-[0.98] ${
                  a.value <= 0 ? 'border-red-600 bg-red-950/60' : 'border-orange-700/80 bg-orange-950/40'}`}>
                <span className={`text-[10px] font-black uppercase tracking-wider ${a.value <= 0 ? 'text-red-300' : 'text-orange-300'}`}>
                  {a.kind === 'refinery' ? 'Usina' : 'Envasadora'} LV.{a.level} · {a.value <= 0 ? 'quebrada' : `integridade ${a.value}%`}
                </span>
                <span className="shrink-0 rounded-full bg-black px-2 py-0.5 text-[9px] font-black uppercase text-amber-300">Ver e reparar ▸</span>
              </button>
            ))}
          </div>
        )}

        {/* ===== SUB-ABAS ===== */}
        <div className="mx-auto flex max-w-[430px] items-center gap-2" data-testid="oil-empire-subtabs">
          {[{ id: 'usina', img: 'usina' }, { id: 'envasadora', img: 'envasadora' }].map((t) => (
            <button key={t.id} onClick={() => { if (subTab !== t.id) sfxTab(); setSubTab(t.id); }}
              data-testid={`oil-subtab-${t.id}`} aria-label={t.id}
              className="min-w-0 flex-1 transition-transform active:scale-95">
              <img src={oilArt(`tab_${t.img}_${subTab === t.id ? 'on' : 'off'}`, locale)} alt={t.id}
                className={`w-full drop-shadow-lg transition-opacity ${subTab === t.id ? '' : 'opacity-75'}`} />
            </button>
          ))}
        </div>

        {/* ===== SUB-ABA USINA (fica montada: alternar não repinta o painel) ===== */}
        <div style={{ display: subTab === 'usina' ? 'block' : 'none' }}>
          <div className="overflow-hidden rounded-2xl border border-amber-700/50 bg-black/65 shadow-2xl backdrop-blur-sm" data-testid="oil-usina-panel">
            <div className="flex items-center justify-between px-4 pt-3">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400">
                Usina <span className={`ml-1 rounded px-1.5 py-0.5 text-[9px] ${owned ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>LV.{lvl}</span>
              </p>
              <span className="text-[9px] font-black text-zinc-400">
                {refLvl > 0 ? `Suas usinas: LV.1–${refLvl}` : 'Nenhuma usina comprada'}
              </span>
            </div>

            {/* carrossel */}
            <div className="relative px-2" data-testid="oil-usina-carousel">
              <img src={`/oil/usina_lv${lvl}.webp`} alt={`Usina LV${lvl}`} data-testid="oil-usina-img"
                className={`mx-auto w-full max-w-[340px] drop-shadow-2xl transition-[filter,opacity] duration-200 ${owned ? (refInteg <= 0 ? 'brightness-[0.45] grayscale-[0.85]' : '') : 'brightness-[0.22] grayscale'} ${drilling ? 'animate-pulse' : ''}`} />
              {owned && <IntegritySeal kind="refinery" value={refInteg} />}
              {owned && refInteg <= 0 && <BrokenStamp kind="refinery" />}
              {/* pré-decodificadas: alternar as setas não recarrega nada */}
              <div className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0" aria-hidden>
                {[1, 2, 3, 4, 5].map((n) => <img key={n} src={`/oil/usina_lv${n}.webp`} alt="" />)}
              </div>
              {!owned && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="rounded-xl border border-zinc-700 bg-black/80 px-4 py-2 text-xs font-black text-zinc-300">🔒 USINA LV.{lvl} — NÃO COMPRADA</span>
                </div>
              )}
              {drilling && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="animate-bounce text-5xl drop-shadow-[0_0_12px_rgba(245,158,11,0.8)]">🛢️</span>
                </div>
              )}
              {/* dots dos níveis */}
              <div className="mt-1 flex justify-center gap-1.5">
                {Array.from({ length: maxLvl }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setSelLvl(n)} data-testid={`oil-usina-dot-${n}`}
                    className={`h-1.5 rounded-full transition-all ${n === lvl ? 'w-5 bg-amber-400' : n <= refLvl ? 'w-1.5 bg-amber-700' : 'w-1.5 bg-zinc-700'}`} />
                ))}
              </div>
              {owned && (
                <div className="px-2 pb-1">
                  <Integrity kind="refinery" level={lvl} value={refInteg} busy={busy} coins={player.black_coins || 0}
                    cost={repairCost('refinery', lvl)} onRepair={() => doRepair('refinery', lvl)} />
                </div>
              )}
            </div>

            <div className="px-4 pb-4 pt-2">
              {owned && refInteg > 0 && !drilling && (
                <QtyPicker kind="extract" qty={exQtyEff} setQty={setExQty} max={exMax} tone="amber" />
              )}
              <div className="flex items-stretch gap-2">
                {/* seta esquerda */}
                <button onClick={() => { sfxClose(); setSelLvl(Math.max(1, lvl - 1)); setLastDrops(null); }} disabled={lvl <= 1} data-testid="oil-usina-prev"
                  className={`w-12 shrink-0 rounded-xl border text-lg font-black transition-transform active:scale-95 ${lvl <= 1 ? 'border-zinc-800 bg-black/40 text-zinc-700' : 'border-amber-600/70 bg-amber-950/60 text-amber-300'}`}>
                  ◀
                </button>
                {/* botão central */}
                {refLvl === 0 && lvl === 1 ? (
                  <button disabled={busy} data-testid="oil-buy-refinery"
                    onClick={() => run(() => oilApi.post('/upgrade/refinery', { command_id: genCmd() }),
                      () => { playClip(SFX_SPEND); sfx.build(); gameNotice.ok('Usina construída!', 'Sua primeira usina de perfuração está de pé. Bora extrair.'); setSelLvl(1); })}
                    className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 py-3.5 text-sm font-black text-black shadow-lg shadow-amber-900/50 transition-transform active:scale-[0.98]">
                    <span className="flex items-center justify-center gap-2">
                      <span>CONSTRUIR USINA</span>
                      <span className="flex items-center gap-1 rounded-full bg-black px-2 py-0.5 text-[10px] font-black text-red-500">
                        -{fmt(refNext?.purchase_or_upgrade_black_coins)} <OilIcon k="black" />
                      </span>
                    </span>
                  </button>
                ) : owned ? (
                  refInteg <= 0 ? (
                    <button disabled data-testid="oil-usina-broken"
                      className="flex-1 rounded-xl border border-red-700 bg-red-950/60 py-3 text-xs font-black uppercase leading-tight tracking-wider text-red-300">
                      Usina LV.{lvl} fora de operação
                      <span className="mt-0.5 block text-[9px] font-bold normal-case tracking-normal text-red-200/80">
                        Pague o reparo para voltar a extrair
                      </span>
                    </button>
                  ) : (
                  <button disabled={busy || drilling || !canExtract} onClick={doExtract} data-testid="oil-extract-btn"
                    className={`relative flex-1 overflow-hidden rounded-xl py-2.5 text-center shadow-lg transition-transform active:scale-[0.98] disabled:active:scale-100 ${
                      canExtract
                        ? 'border border-amber-300/80 bg-gradient-to-b from-amber-400 via-amber-500 to-orange-600 shadow-amber-950/70'
                        : 'cursor-not-allowed border border-zinc-700 bg-zinc-900/80 shadow-none'}`}>
                    {!canExtract && !drilling ? (
                      <span className="flex flex-col items-center gap-1 leading-tight" data-testid="oil-extract-no-coins">
                        <span className="whitespace-nowrap text-sm font-black uppercase tracking-[0.1em] text-zinc-400">
                          Sem moedas pretas
                        </span>
                        <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] font-black text-red-400">
                          {`faltam ${fmt(exShort)}`} <OilIcon k="black" />
                        </span>
                      </span>
                    ) : drilling ? (
                      <span className="flex items-center justify-center gap-2 text-sm font-black text-black">
                        <span className="inline-block animate-spin">⚙️</span> PERFURANDO...
                      </span>
                    ) : (
                      <span className="flex flex-col items-center gap-1 leading-tight">
                        <span className="whitespace-nowrap text-base font-black tracking-[0.12em] text-black drop-shadow-sm"
                          data-testid="oil-extract-label">
                          EXTRAIR{exQtyEff > 1 ? ` ${exQtyEff}` : ' PETRÓLEO'}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] font-black text-red-500">
                            -{fmt((selCfg?.extraction_cost_black_coins || 0) * exQtyEff)} <OilIcon k="black" />
                          </span>
                          <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] font-black text-emerald-300">
                            +{fmt((selCfg?.drop_min || 0) * exQtyEff)}–{fmt((selCfg?.drop_max || 0) * exQtyEff)} <OilIcon k="drops" />
                          </span>
                        </span>
                      </span>
                    )}
                    {drilling && <div className="absolute bottom-0 left-0 h-1 w-full animate-[oilbar_1.4s_linear_forwards] bg-black/70" />}
                  </button>
                  )
                ) : isNext ? (
                  <button disabled={busy} data-testid="oil-upgrade-refinery"
                    onClick={() => run(() => oilApi.post('/upgrade/refinery', { command_id: genCmd() }),
                      (d) => { playClip(SFX_SPEND); sfx.build(); gameNotice.ok(`Usina LV${d.level} comprada!`, 'Poço mais fundo, mais gotas por extração.'); setSelLvl(d.level); })}
                    className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 py-3.5 text-sm font-black text-black shadow-lg shadow-amber-900/50 transition-transform active:scale-[0.98]">
                    <span className="flex items-center justify-center gap-2">
                      <span>COMPRAR USINA LV.{lvl}</span>
                      <span className="flex items-center gap-1 rounded-full bg-black px-2 py-0.5 text-[10px] font-black text-red-500">
                        -{fmt(selCfg?.purchase_or_upgrade_black_coins)} <OilIcon k="black" />
                      </span>
                    </span>
                  </button>
                ) : (
                  <button disabled data-testid="oil-usina-locked"
                    className="flex-1 rounded-xl border border-zinc-800 bg-black/60 py-3.5 text-sm font-black text-zinc-500">
                    🔒 COMPRE A USINA LV.{refLvl + 1} PRIMEIRO
                  </button>
                )}
                {/* seta direita */}
                <button onClick={() => { sfxClose(); setSelLvl(Math.min(maxLvl, lvl + 1)); setLastDrops(null); }} disabled={lvl >= maxLvl} data-testid="oil-usina-next"
                  className={`w-12 shrink-0 rounded-xl border text-lg font-black transition-transform active:scale-95 ${lvl >= maxLvl ? 'border-zinc-800 bg-black/40 text-zinc-700' : 'border-amber-600/70 bg-amber-950/60 text-amber-300'}`}>
                  ▶
                </button>
              </div>
              {lastDrops !== null && !drilling && (
                <p className="mt-2 text-center text-xs font-bold" data-testid="oil-last-drops">
                  {lastDrops > 0
                    ? <span className="text-emerald-400">Última extração: +{lastDrops} gotas <OilIcon k="drops" /></span>
                    : <span className="text-zinc-400">Poço seco 💨</span>}
                </p>
              )}
              {!owned && selCfg && (
                <p className="mt-1 text-center text-[10px] text-zinc-500">
                  LV.{lvl}: rende de {selCfg.drop_min} a {selCfg.drop_max} <OilIcon k="drops" /> · extração <OilIcon k="black" /> {fmt(selCfg.extraction_cost_black_coins)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ===== SUB-ABA ENVASADORA (idem) ===== */}
        <div className="space-y-4" style={{ display: subTab === 'envasadora' ? 'block' : 'none' }}>
          <>
            <div className="overflow-hidden rounded-2xl border border-emerald-700/50 bg-black/65 shadow-2xl backdrop-blur-sm" data-testid="oil-envasadora-panel">
              <div className="flex items-center justify-between px-4 pt-3">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-400">
                  Envasadora <span className={`ml-1 rounded px-1.5 py-0.5 text-[9px] ${bOwned ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>LV.{bLvl}</span>
                </p>
                <span className="text-[9px] font-black text-zinc-400">
                  {botLvl > 0 ? `Suas envasadoras: LV.1–${botLvl}` : 'Nenhuma envasadora'}
                </span>
              </div>
              <div className="relative px-2" data-testid="oil-envasadora-carousel">
                <img src={`/oil/envasadora_lv${bLvl}.webp`} alt={`Envasadora LV${bLvl}`}
                  className={`mx-auto w-full max-w-[300px] drop-shadow-2xl transition-[filter,opacity] duration-200 ${bOwned ? (botInteg <= 0 ? 'brightness-[0.45] grayscale-[0.85]' : '') : 'brightness-[0.22] grayscale'}`} />
                {bOwned && <IntegritySeal kind="bottling" value={botInteg} />}
                {bOwned && botInteg <= 0 && <BrokenStamp kind="bottling" />}
                <div className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0" aria-hidden>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <React.Fragment key={n}>
                      <img src={`/oil/envasadora_lv${n}.webp`} alt="" />
                      <img src={`/oil/barril_lv${n}.webp`} alt="" />
                    </React.Fragment>
                  ))}
                </div>
                {!bOwned && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-xl border border-zinc-700 bg-black/80 px-4 py-2 text-xs font-black text-zinc-300">🔒 ENVASADORA LV.{bLvl} — NÃO COMPRADA</span>
                  </div>
                )}
                <div className="absolute bottom-6 right-3 flex flex-col items-center rounded-xl border border-emerald-600/60 bg-black/80 p-1.5 shadow-lg" data-testid="oil-barrel-badge">
                  <img src={`/oil/barril_lv${bLvl}.webp`} alt={`Barril LV${bLvl}`}
                    className={`h-12 w-12 object-contain drop-shadow ${bOwned ? '' : 'grayscale opacity-50'}`} />
                  <span className="mt-0.5 text-[8px] font-black uppercase tracking-wider text-emerald-300">Barril LV.{bLvl}</span>
                </div>
                <div className="mt-1 flex justify-center gap-1.5">
                  {Array.from({ length: maxBotLvl }, (_, i) => i + 1).map((n) => (
                    <button key={n} onClick={() => setSelBotLvl(n)} data-testid={`oil-envasadora-dot-${n}`}
                      className={`h-1.5 rounded-full transition-all ${n === bLvl ? 'w-5 bg-emerald-400' : n <= botLvl ? 'w-1.5 bg-emerald-700' : 'w-1.5 bg-zinc-700'}`} />
                  ))}
                </div>
              </div>
              <div className="px-4 pb-4 pt-2">
                {bOwned && (
                  <Integrity kind="bottling" level={bLvl} value={botInteg} busy={busy} coins={player.black_coins || 0}
                    cost={repairCost('bottling', bLvl)} onRepair={() => doRepair('bottling', bLvl)} />
                )}
                {bOwned && botInteg > 0 && (
                  <QtyPicker kind="bottle" qty={botQtyEff} setQty={setBotQty} max={botMax} tone="emerald" />
                )}
                <div className="mt-2 flex items-stretch gap-2">
                  <button onClick={() => { sfxClose(); setSelBotLvl(Math.max(1, bLvl - 1)); }} disabled={bLvl <= 1} data-testid="oil-envasadora-prev"
                    className={`w-12 shrink-0 rounded-xl border text-lg font-black transition-transform active:scale-95 ${bLvl <= 1 ? 'border-zinc-800 bg-black/40 text-zinc-700' : 'border-emerald-600/70 bg-emerald-950/60 text-emerald-300'}`}>
                    ◀
                  </button>
                  {bOwned ? (
                    botInteg <= 0 ? (
                      <button disabled data-testid="oil-envasadora-broken"
                        className="flex-1 rounded-xl border border-red-700 bg-red-950/60 py-3 text-xs font-black uppercase leading-tight tracking-wider text-red-300">
                        Envasadora LV.{bLvl} fora de operação
                        <span className="mt-0.5 block text-[9px] font-bold normal-case tracking-normal text-red-200/80">
                          Pague o reparo para voltar a envasar
                        </span>
                      </button>
                    ) : (
                    <button disabled={busy || !canBottle} data-testid={`oil-bottle-lv${bLvl}`}
                      onClick={doBottle}
                      className="relative flex-1 rounded-xl border border-emerald-300/80 bg-gradient-to-b from-emerald-400 via-emerald-500 to-teal-600 py-2.5 text-center shadow-lg shadow-emerald-950/70 transition-transform active:scale-[0.98] disabled:opacity-50">
                      <span className="flex flex-col items-center gap-1 leading-tight">
                        <span className="whitespace-nowrap text-base font-black tracking-[0.12em] text-black drop-shadow-sm"
                          data-testid="oil-bottle-label">
                          ENVASAR{botQtyEff > 1 ? ` ${botQtyEff}` : ''}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] font-black text-red-500">
                            -{fmt((bCfg?.oil_drops_required || 0) * botQtyEff)} <OilIcon k="drops" />
                          </span>
                          <span className="flex items-center gap-1 rounded-full bg-black px-2.5 py-0.5 text-[10px] font-black text-amber-300">
                            +{botQtyEff} {botQtyEff > 1 ? 'barris' : 'barril'} LV.{bLvl}
                            <img src={`/oil/barril_lv${bLvl}.webp`} alt="" className="h-3.5 w-auto" />
                          </span>
                        </span>
                      </span>
                    </button>
                    )
                  ) : bIsNext ? (
                    <button disabled={busy} data-testid="oil-upgrade-bottling"
                      onClick={() => run(() => oilApi.post('/upgrade/bottling', { command_id: genCmd() }),
                        (d) => { playClip(SFX_SPEND); sfx.build(); gameNotice.ok(`Envasadora LV${d.level} comprada!`, 'Barris melhores valem mais FOX Dólar na Bolsa.'); setSelBotLvl(d.level); })}
                      className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 py-3.5 text-sm font-black text-black shadow-lg shadow-amber-900/50 transition-transform active:scale-[0.98]">
                      <span className="flex items-center justify-center gap-2">
                        <span>{botLvl === 0 ? 'CONSTRUIR' : 'COMPRAR'} ENVASADORA LV.{bLvl}</span>
                        <span className="flex items-center gap-1 rounded-full bg-black px-2 py-0.5 text-[10px] font-black text-red-500">
                          -{fmt(botSelCfg?.purchase_or_upgrade_black_coins)} <OilIcon k="black" />
                        </span>
                      </span>
                    </button>
                  ) : (
                    <button disabled data-testid="oil-envasadora-locked"
                      className="flex-1 rounded-xl border border-zinc-800 bg-black/60 py-3.5 text-sm font-black text-zinc-500">
                      🔒 COMPRE A ENVASADORA LV.{botLvl + 1} PRIMEIRO
                    </button>
                  )}
                  <button onClick={() => { sfxClose(); setSelBotLvl(Math.min(maxBotLvl, bLvl + 1)); }} disabled={bLvl >= maxBotLvl} data-testid="oil-envasadora-next"
                    className={`w-12 shrink-0 rounded-xl border text-lg font-black transition-transform active:scale-95 ${bLvl >= maxBotLvl ? 'border-zinc-800 bg-black/40 text-zinc-700' : 'border-emerald-600/70 bg-emerald-950/60 text-emerald-300'}`}>
                    ▶
                  </button>
                </div>
                <p className="mt-2 text-center text-[10px] text-zinc-400">
                  {bOwned && (
                    <span className="mb-1 block text-[9px] font-bold text-amber-300/90" data-testid="oil-bottle-worth">
                      Vale {fmt((bCfg?.fox_dollar_value || 0) * botQtyEff)} FOX Dólar quando vendido na Bolsa
                    </span>
                  )}
                  Suas gotas: <b className="text-zinc-100"><OilIcon k="drops" /> {fmt(player.drops)}</b>
                  {bOwned && !canBottle && <span className="ml-2 text-red-400">· gotas insuficientes</span>}
                </p>
              </div>
            </div>

            {/* ===== BOLSA + INVENTÁRIO ===== */}
            <div className="rounded-2xl border border-zinc-700/60 bg-black/65 p-4 shadow-2xl backdrop-blur-sm" data-testid="oil-bolsa-panel">
              <p className="mb-3 text-[10px] font-black uppercase tracking-[0.25em] text-zinc-300">
                📈 Bolsa do Petróleo · Inventário ({barrels.length})
              </p>
              <div className="grid grid-cols-5 gap-1.5" data-testid="oil-inventory">
                {[1, 2, 3, 4, 5].map((n) => {
                  const qty = invCount[n] || 0;
                  const sel = sellLvl === n;
                  return (
                    <button key={n} disabled={qty === 0} onClick={() => { setSellLvl(n); setSellQty(1); }} data-testid={`oil-inv-lv${n}`}
                      className={`relative rounded-xl border p-1.5 transition-all ${sel ? 'border-amber-400 bg-amber-950/60 shadow-lg shadow-amber-900/40' : qty > 0 ? 'border-zinc-700 bg-black/50' : 'border-zinc-800 bg-black/30 opacity-40'}`}>
                      <img src={`/oil/barril_lv${n}.webp`} alt={`Barril LV${n}`} className={`mx-auto h-10 w-10 object-contain ${qty === 0 ? 'grayscale' : ''}`} />
                      <p className="mt-0.5 text-center text-[9px] font-black text-zinc-300">LV.{n}</p>
                      <span className={`absolute -right-1 -top-1 rounded-full px-1.5 py-0.5 text-[9px] font-black ${qty > 0 ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-500'}`}>×{qty}</span>
                    </button>
                  );
                })}
              </div>
              {sellLvl && (invCount[sellLvl] || 0) > 0 ? (
                <div className="mt-3 rounded-xl border border-amber-800/50 bg-black/50 p-3" data-testid="oil-sell-box">
                  <p className="text-[10px] font-bold text-zinc-300">
                    Barril LV.{sellLvl} · valor unitário: <b className="text-amber-300">{fmt(unitVal)} <OilIcon k="fox" /></b>
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => setSellQty((q) => Math.max(1, q - 1))} data-testid="oil-sell-minus"
                      className="h-9 w-9 rounded-lg border border-zinc-700 bg-black text-lg font-black text-zinc-300 active:scale-95">−</button>
                    <input type="number" min={1} max={invCount[sellLvl]} value={sellQty} data-testid="oil-sell-qty"
                      onChange={(e) => setSellQty(Math.max(1, Math.min(invCount[sellLvl], parseInt(e.target.value, 10) || 1)))}
                      className="h-9 w-16 rounded-lg border border-zinc-700 bg-black text-center text-sm font-black text-white" />
                    <button onClick={() => setSellQty((q) => Math.min(invCount[sellLvl], q + 1))} data-testid="oil-sell-plus"
                      className="h-9 w-9 rounded-lg border border-zinc-700 bg-black text-lg font-black text-zinc-300 active:scale-95">+</button>
                    <button onClick={() => setSellQty(invCount[sellLvl])} data-testid="oil-sell-max"
                      className="h-9 rounded-lg border border-amber-700/70 bg-amber-950/50 px-3 text-[10px] font-black text-amber-300 active:scale-95">MÁX</button>
                  </div>
                  <button disabled={busy} data-testid="oil-sell-btn"
                    onClick={() => run(() => oilApi.post('/sell-bulk', { command_id: genCmd(), barrel_level: sellLvl, quantity: sellQty }),
                      (d) => { playClip('/oil/sfx_venda.mp3'); launchFly('fox', '[data-testid="oil-sell-btn"]', '[data-testid="oil-hud-fox"]', d.sold); gameNotice.ok(`${d.sold} barril(is) vendido(s)!`, `+${fmt(d.fox)} FOX Dólar na sua fortuna.`, '/oil/ic_fox.webp'); setSellQty(1); })}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-600 py-2.5 text-xs font-black text-black shadow transition-transform active:scale-[0.98]">
                    VENDER {sellQty} BARRIL{sellQty > 1 ? 'S' : ''} · +{fmt(unitVal * sellQty)} <OilIcon k="fox" />
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-center text-[10px] text-zinc-500" data-testid="oil-inventory-hint">
                  {barrels.length === 0 ? 'Nenhum barril envasado. Extraia e envase! 🛢️' : 'Toque em um barril do inventário para vender.'}
                </p>
              )}
            </div>
          </>
        </div>
      </div>
      <style>{`@keyframes oilbar { from { width: 0 } to { width: 100% } }
@keyframes usina-in { from { opacity: 0.3; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
    </div>
  );
}

export default React.memo(OilEmpire);
